import {Box, Button, Divider, FormControlLabel, Switch, TextField, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import React, {useEffect, useState} from 'react';
import {useProfile} from 'crqlar-react-common/dist/utils/hooks/profile/useProfiles';
import {PaymentMethod} from 'crqlar-react-common/dist/models/billing/paymentMethod.model';
import {PaymentMethodService} from 'crqlar-react-common/dist/services/billing/paymentMethod.service';
import {useQuery} from 'react-query';
import ProfileInfoRow from 'crqlar-react-common/dist/components/ProfileInfoRow';
import {Folio} from "crqlar-react-common/models/billing/folio.model";
import {FOLIO_FIELD_GAP} from "../editor/folioStyles";
import {getPrimaryEmail} from "crqlar-react-common/dist/utils/profile.util";

interface StripeProviderProps {
    onDataChange: (isValid: boolean, data: any) => void;
    profileId?: string;
    folio?: Folio;
    /** The folio dialog's payment block: small fields, the names below the email, 13px text. */
    compact?: boolean;
}

const StripeProvider = ({onDataChange, profileId: initialProfileId, folio, compact}: StripeProviderProps) => {
    const {t} = useTranslation();
    const fieldSize = compact ? 'small' : undefined;
    const textSx = compact ? {fontSize: 13} : undefined;
    const [selectedProfileId, setSelectedProfileId] = useState<string | undefined>(initialProfileId);
    const profile = useProfile(selectedProfileId);

    // States for form data
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [emailTo, setEmailTo] = useState('');
    const [chargeImmediately, setChargeImmediately] = useState(true);
    const [sendEmail, setSendEmail] = useState(true);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>(undefined);
    const [useProfilePaymentMethod, setUseProfilePaymentMethod] = useState<boolean>(true);

    useEffect(() => {
        setFirstName(folio?.billTo?.firstName || '');
        setLastName(folio?.billTo?.lastName || '');
        setEmailTo(folio?.billTo?.email || '');
        setChargeImmediately(true);
        setSendEmail(true);
    }, [folio?.id]);

    // Update local profile state when the prop changes
    useEffect(() => {
        setSelectedProfileId(initialProfileId);
    }, [initialProfileId]);

    // Fetch payment methods from the folio
    const folioPaymentMethodsQuery = useQuery(
        ['folioPaymentMethods', folio?.id, 'stripe'],
        async () => {
            if (!folio?.id) return [];

            const response = await PaymentMethodService.getAllPaymentMethods({
                folioIds: [folio?.id],
                providers: ['stripe'],
                limit: {
                    offset: 0,
                    limit: 100,
                }
            });

            return response.content || [];
        },
        {
            enabled: !!folio?.id,
        }
    );

    // Fetch payment methods for this profile
    const profilePaymentMethodsQuery = useQuery(
        ['paymentMethods', selectedProfileId, 'stripe'],
        async () => {
            if (!selectedProfileId) return [];

            const response = await PaymentMethodService.getAllPaymentMethods({
                profileIds: [selectedProfileId],
                providers: ['stripe'],
                limit: {
                    offset: 0,
                    limit: 100,
                }
            });

            return response.content || [];
        },
        {
            enabled: !!selectedProfileId && (!folioPaymentMethodsQuery.data || folioPaymentMethodsQuery.data.length === 0 || !useProfilePaymentMethod),
        }
    );

    // Use folio payment methods or fall back to profile payment methods
    const folioPaymentMethods = folioPaymentMethodsQuery.data || [];
    const profilePaymentMethods = profilePaymentMethodsQuery.data || [];
    const paymentMethods = useProfilePaymentMethod && folioPaymentMethods.length > 0
        ? folioPaymentMethods
        : profilePaymentMethods;

    // Determine if we're loading payment methods
    const isLoadingPaymentMethods =
        (useProfilePaymentMethod && folioPaymentMethodsQuery.isLoading) ||
        (!useProfilePaymentMethod && profilePaymentMethodsQuery.isLoading);

    // Reset payment method when sources change
    useEffect(() => {
        setSelectedPaymentMethod(undefined);
    }, [selectedProfileId, useProfilePaymentMethod]);

    // Auto-select the first folio payment method if there's only one
    useEffect(() => {
        if (folioPaymentMethods.length === 1 && useProfilePaymentMethod) {
            setSelectedPaymentMethod(folioPaymentMethods[0]);
        }
    }, [folioPaymentMethods, useProfilePaymentMethod]);

    // Initialize form with profile data when it loads
    useEffect(() => {
        if (profile) {
            setFirstName(folio?.billTo?.firstName || profile.firstName || '');
            setLastName(folio?.billTo?.lastName || profile.lastName || '');
            setEmailTo(folio?.billTo?.email || getPrimaryEmail(profile) || '');
        }
    }, [profile]);

    // Validate form and pass data to parent
    useEffect(() => {
        const isValid = !!emailTo &&
            (!!selectedPaymentMethod || paymentMethods.length === 0)

        const data = {
            firstName,
            lastName,
            email: emailTo,
            paymentMethodId: selectedPaymentMethod?.id,
            chargeImmediately,
            sendEmail,
            provider: 'stripe',
            profileId: selectedProfileId
        };

        onDataChange(isValid, data);
    }, [
        firstName,
        lastName,
        emailTo,
        selectedPaymentMethod,
        chargeImmediately,
        sendEmail,
        selectedProfileId,
        onDataChange,
        paymentMethods.length
    ]);

    return (
        <Box
            sx={{
                gap: compact ? FOLIO_FIELD_GAP : '12px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {folioPaymentMethods.length > 0 && (
                <FormControlLabel
                    control={
                        <Switch
                            size={fieldSize}
                            checked={useProfilePaymentMethod}
                            onChange={(e) => setUseProfilePaymentMethod(e.target.checked)}
                        />
                    }
                    label={t('Use payment methods linked to this folio')}
                    componentsProps={compact ? {typography: textSx} : undefined}
                />
            )}

            {/*{(!useProfilePaymentMethod || folioPaymentMethods.length === 0) && (*/}
            {/*    <>*/}
            {/*        <ProfileInfoRow*/}
            {/*            id={'stripe-profile-selector'}*/}
            {/*            title={t('Profile')}*/}
            {/*            profileId={selectedProfileId}*/}
            {/*            setProfileId={setSelectedProfileId}*/}
            {/*            onDelete={() => setSelectedProfileId(undefined)}*/}
            {/*        />*/}

            {/*        <Divider sx={{my: 1}}/>*/}
            {/*    </>*/}
            {/*)}*/}

            {!useProfilePaymentMethod && !selectedProfileId ? (
                <Typography color="warning.main" sx={textSx}>
                    {t('Please select a profile to see available payment methods')}
                </Typography>
            ) : (
                <>
                    <Typography sx={compact ? {...textSx, color: 'text.secondary'} : undefined}>
                        {t("Invoice to")}
                    </Typography>

                    {/* Side by side in a dialog; in the narrow panel the names go below the email */}
                    <Box
                        sx={{
                            gap: compact ? FOLIO_FIELD_GAP : '24px',
                            width: '100%',
                            display: 'flex',
                            ...(compact && {flexWrap: 'wrap'}),
                        }}
                    >
                        <TextField
                            size={fieldSize}
                            label={t('Email')}
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            sx={compact ? {flex: '1 1 100%'} : {flex: 1}}
                        />

                        <TextField
                            size={fieldSize}
                            label={t('First name')}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            sx={{flex: 1}}
                        />

                        <TextField
                            size={fieldSize}
                            label={t('Last name')}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            sx={{flex: 1}}
                        />
                    </Box>

                    {isLoadingPaymentMethods ? (
                        <Typography variant="body2">{t('Loading payment methods...')}</Typography>
                    ) : paymentMethods.length > 0 ? (
                        <TextField
                            select
                            fullWidth
                            size={fieldSize}
                            label={t('Payment method')}
                            value={selectedPaymentMethod?.id || ''}
                            onChange={(e) => {
                                const selectedId = e.target.value;
                                setSelectedPaymentMethod(paymentMethods.find(pm => pm.id === selectedId));
                            }}
                            SelectProps={{
                                native: true,
                            }}
                            // A native select shows the empty option's text: in the new dialog the label stays
                            // above it (the legacy dialogs keep theirs as they were)
                            InputLabelProps={compact ? {shrink: true} : undefined}
                        >
                            <option value="">{t('Select a payment method')}</option>
                            {paymentMethods.map((pm) => (
                                <option key={pm.id} value={pm.id}>
                                    **** **** **** {pm.card?.last4} ({pm.card?.expMonth}/{pm.card?.expYear})
                                </option>
                            ))}
                        </TextField>
                    ) : (
                        <Typography color="warning.main" sx={textSx}>
                            {useProfilePaymentMethod
                                ? t('No Stripe payment methods linked to this folio. Try using profile payment methods instead.')
                                : t('No Stripe payment methods available for this profile')}
                        </Typography>
                    )}

                    {useProfilePaymentMethod && folioPaymentMethods.length === 0 && (
                        <Button
                            variant="text"
                            color="primary"
                            size={fieldSize}
                            onClick={() => setUseProfilePaymentMethod(false)}
                            sx={{alignSelf: 'flex-start'}}
                        >
                            {t('Use profile payment methods instead')}
                        </Button>
                    )}

                    {!!selectedPaymentMethod && (
                        <FormControlLabel
                            control={
                                <Switch
                                    size={fieldSize}
                                    checked={chargeImmediately}
                                    onChange={(e) => setChargeImmediately(e.target.checked)}
                                />
                            }
                            label={t('Charge immediately if possible')}
                            componentsProps={compact ? {typography: textSx} : undefined}
                        />
                    )}

                    <FormControlLabel
                        control={
                            <Switch
                                size={fieldSize}
                                checked={sendEmail}
                                onChange={(e) => setSendEmail(e.target.checked)}
                            />
                        }
                        label={t('Send email to customer')}
                        componentsProps={compact ? {typography: textSx} : undefined}
                    />
                </>
            )}
        </Box>
    );
};

export default StripeProvider;